import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from '@tanstack/react-query'; // Import useQuery
import { ThreeDot } from 'react-loading-indicators';
import CategoryFilter from '../Components/FilterComponents/CategoryFilter';
import PriceRangeFilter from '../Components/FilterComponents/PriceRangeFilter';
import ConditionFilter from '../Components/FilterComponents/ConditionFilter';
import LocationFilter from '../Components/FilterComponents/LocationFilter';
import ApplyFilters from '../Components/FilterComponents/ApplyFiltersButton';
import AdCard from '../Components/AdCard';
import { Container } from 'react-bootstrap';
import { Pagination } from '@mui/material';
import './AdsPage.css';

const ITEMS_PER_PAGE = 12;

// Helper function to parse URL parameters with defaults (remains the same)
const parseUrlParams = (searchString) => {
  const params = new URLSearchParams(searchString);
  const priceRangeParam = [
    params.get('minPrice') ? parseInt(params.get('minPrice'), 10) : 0,
    params.get('maxPrice') ? parseInt(params.get('maxPrice'), 10) : 9999999,
  ];
  if (priceRangeParam[0] > priceRangeParam[1]) {
    priceRangeParam[0] = 0;
  }
  const itemConditionParam = params.get('itemCondition') ? params.get('itemCondition').split(',') : [];
  const cityParam = params.get('city') ? params.get('city').split(',') : [];

  return {
    title: params.get("title") || "",
    categoryId: params.get('categoryId') || '',
    subcategory: '', // This might be handled differently or not directly from URL
    priceRange: priceRangeParam,
    itemCondition: itemConditionParam,
    region: params.get('region') || '',
    city: cityParam,
    page: parseInt(params.get('page'), 10) || 1,
    sort: params.get('sort') || 'newest',
  };
};

// --- API Fetching Functions (moved outside component for clarity) ---
const fetchAdsAPI = async (queryParams) => {
  const params = new URLSearchParams({
    page: queryParams.page,
    limit: ITEMS_PER_PAGE,
    sort: queryParams.sort,
  });

  if (queryParams.categoryId) params.append('categoryId', queryParams.categoryId);
  // Send null or skip if default values, adjust based on your API's expectation
  if (queryParams.minPrice !== null && queryParams.minPrice !== 0) {
    params.append('minPrice', queryParams.minPrice);
  }
  if (queryParams.maxPrice !== null && queryParams.maxPrice !== 9999999) {
    params.append('maxPrice', queryParams.maxPrice);
  }
  if (queryParams.itemCondition?.length > 0) {
    params.append('itemCondition', queryParams.itemCondition.join(','));
  }
  if (queryParams.region) params.append('region', queryParams.region);
  // Your original code used 'city' for main item search, and 'city_id' for price range
  if (queryParams.city?.length) params.append('city', queryParams.city.join(','));
  if (queryParams.title) {
    params.append('title', queryParams.title);
  }

  const apiUrl = `${process.env.REACT_APP_API_URL}/listing/search?${params.toString()}`;
  const response = await fetch(apiUrl, { method: 'GET', credentials: 'include' });
  if (!response.ok) throw new Error(`Napaka pri pridobivanju oglasov! Status: ${response.status}`);
  return response.json();
};

const fetchPriceRangeAPI = async (filters) => {
  const params = new URLSearchParams();
  const categoryIdForPriceRange = filters.subcategory || filters.category;

  if (categoryIdForPriceRange) params.append('categoryId', categoryIdForPriceRange);
  if (filters.title) params.append('title', filters.title);
  if (filters.region) params.append('region', filters.region);
  if (filters.city?.length) params.append('city_id', filters.city.join(',')); // API uses city_id here

  const response = await fetch(`${process.env.REACT_APP_API_URL}/listings/pricerange?${params.toString()}`);
  if (!response.ok) {
    console.error(`Failed to fetch price range. Status: ${response.status}`);
    return { minPrice: 0, maxPrice: 9999999 }; // Return default or throw
  }
  const data = await response.json();
  return {
    minPrice: data.minPrice ?? 0,
    maxPrice: data.maxPrice ?? 9999999,
  };
};


const AdsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const prevPriceRangeContext = useRef(null);

  const urlParams = useMemo(() => parseUrlParams(location.search), [location.search]);
  const {
    title: appliedSearchTitle,
    categoryId: appliedCategoryId,
    priceRange: appliedPriceRange,
    itemCondition: appliedItemCondition,
    region: appliedRegion,
    city: appliedCity,
    page: appliedPage,
    sort: appliedSortOrder,
  } = urlParams;

  const [uiFilters, setUiFilters] = useState(() => {
    const initialParams = parseUrlParams(location.search);
    return {
      category: initialParams.categoryId,
      subcategory: '', // Let CategoryFilter manage this
      priceRange: initialParams.priceRange, // This will be initially set by URL, then can be updated by price range query
      itemCondition: initialParams.itemCondition,
      region: initialParams.region,
      city: initialParams.city,
    };
  });

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('viewMode') || 'list');

  const toggleViewMode = () => {
    setViewMode(prev => {
      const newMode = prev === 'list' ? 'grid' : 'list';
      localStorage.setItem('viewMode', newMode);
      return newMode;
    });
  };

  // --- Effect to Sync UI filters when URL changes (e.g., back/forward) ---
  useEffect(() => {
    // This effect ensures that if the user navigates (e.g., back/forward),
    // the UI filter inputs are updated to reflect the URL state.
    // However, priceRange in uiFilters is also influenced by the price range query,
    // so we need to be careful not to create loops or overwrite user input unnecessarily.
    setUiFilters(prevUiFilters => ({
      ...prevUiFilters, // Keep existing subcategory if CategoryFilter handles it
      category: appliedCategoryId,
      // priceRange: appliedPriceRange, // Let the price range effect handle this primarily if context changes
      itemCondition: appliedItemCondition,
      region: appliedRegion,
      city: appliedCity,
    }));
  }, [location.search, appliedCategoryId, /*appliedPriceRange,*/ appliedItemCondition, appliedRegion, appliedCity]);


  // --- React Query for Price Range ---
  // Query key dependencies for price range reflect the context for fetching min/max prices
  const priceRangeQueryKeyParams = useMemo(() => ({
    category: uiFilters.category,
    subcategory: uiFilters.subcategory,
    title: appliedSearchTitle, // Use applied title from URL
    region: uiFilters.region,
    city: uiFilters.city, // Use city from uiFilters for context
  }), [uiFilters.category, uiFilters.subcategory, appliedSearchTitle, uiFilters.region, uiFilters.city]);

  const {
    data: priceLimitsData,
    // isLoading: isLoadingPriceRange, // Can be used for specific price range loading indicator
  } = useQuery({
    queryKey: ['priceRange', priceRangeQueryKeyParams],
    queryFn: () => fetchPriceRangeAPI(priceRangeQueryKeyParams),
    staleTime: 1000 * 60 * 5, // Data fresh for 5 mins
    placeholderData: { minPrice: 0, maxPrice: 9999999 }, // Initial default
  });

  // Effect to update uiFilters.priceRange when the *context* for price fetching changes
  useEffect(() => {
    const currentContext = JSON.stringify(priceRangeQueryKeyParams);

    if (priceLimitsData && prevPriceRangeContext.current !== currentContext) {
      setUiFilters(prev => ({
        ...prev,
        priceRange: [priceLimitsData.minPrice, priceLimitsData.maxPrice]
      }));
      prevPriceRangeContext.current = currentContext;
    } else if (priceLimitsData && !prevPriceRangeContext.current) {
      prevPriceRangeContext.current = currentContext; // Initialize on first load with data
    }
  }, [priceLimitsData, priceRangeQueryKeyParams, setUiFilters]);


  // --- React Query for items Data ---
  // Query key uses applied filters from the URL (urlParams)
  const adsQueryKey = useMemo(() => ['items', {
    page: appliedPage,
    sort: appliedSortOrder,
    categoryId: appliedCategoryId,
    minPrice: appliedPriceRange[0] === 0 ? null : appliedPriceRange[0],
    maxPrice: appliedPriceRange[1] === 9999999 ? null : appliedPriceRange[1],
    itemCondition: appliedItemCondition,
    region: appliedRegion,
    city: appliedCity, // This is the city array from the URL
    title: appliedSearchTitle,
  }], [appliedPage, appliedSortOrder, appliedCategoryId, appliedPriceRange, appliedItemCondition, appliedRegion, appliedCity, appliedSearchTitle]);

  const {
    data: adsDataResponse,
    isLoading: isLoadingAds,
    isFetching: isFetchingAds,
    error: adsError,
  } = useQuery({
    queryKey: adsQueryKey,
    queryFn: () => fetchAdsAPI(adsQueryKey[1]), // Pass the params object
    staleTime: 1000 * 60 * 5, // Data fresh for 5 mins
    keepPreviousData: true, // Show old data while fetching new (good for pagination)
  });

  // Derived state for adsData to ensure it always has a valid structure
  const adsData = useMemo(() => {
    return adsDataResponse || { listings: [], totalItems: 0, totalPages: 1 };
  }, [adsDataResponse]);


  // --- Event Handlers (remain largely the same, they update the URL) ---
  const handlePageChange = (event, value) => {
    if (value !== appliedPage) {
      const currentParams = new URLSearchParams(location.search);
      currentParams.set('page', value.toString());
      navigate(`${location.pathname}?${currentParams.toString()}`);
    }
  };

  const handleSortChange = (event) => {
    const newSortOrder = event.target.value;
    if (newSortOrder !== appliedSortOrder) {
      const currentParams = new URLSearchParams(location.search);
      currentParams.set('sort', newSortOrder);
      currentParams.set('page', '1');
      navigate(`${location.pathname}?${currentParams.toString()}`);
    }
  };

  const handleResetSearch = () => {
    const currentParams = new URLSearchParams(location.search);
    if (currentParams.has('title')) {
      currentParams.delete('title');
      currentParams.set('page', '1');
      navigate(`${location.pathname}?${currentParams.toString()}`);
    }
  };

  return (
    <div className="bg-all-items">
      <Container className="items-page">
        <button
          className="filter-toggle d-md-none"
          onClick={() => setMobileFiltersOpen(prev => !prev)}
        >
          {mobileFiltersOpen ? (
            <>Skrij filtre <i className="fa-solid fa-filter" /></>
          ) : (
            <>Pokaži filtre <i className="fa-solid fa-filter" /></>
          )}
        </button>

        <div className={`filters ${mobileFiltersOpen ? 'open' : ''}`}>
          <div className="filter-section">
            <CategoryFilter filters={uiFilters} setFilters={setUiFilters} />
          </div>
          <div className="filter-section">
            <PriceRangeFilter
              filters={uiFilters}
              setFilters={setUiFilters}
              minPrice={priceLimitsData?.minPrice ?? 0}
              maxPrice={priceLimitsData?.maxPrice ?? 9999999}
            // Add a key if PriceRangeFilter internal state needs reset on min/max change
            // key={`${priceLimitsData?.minPrice}-${priceLimitsData?.maxPrice}`}
            />
          </div>
          <div className="filter-section">
            <ConditionFilter filters={uiFilters} setFilters={setUiFilters} />
          </div>
          <div className="filter-section">
            <LocationFilter filters={uiFilters} setFilters={setUiFilters} selectedRegion={uiFilters.region} />
          </div>
          <div className="apply-filters">
            <ApplyFilters uiFilters={uiFilters} appliedSearchTitle={appliedSearchTitle} />
          </div>
        </div>

        {isLoadingAds ? ( // Main loading indicator for items
          <ThreeDot variant="bounce" color="#FF4367" size="medium" text="Nalaganje" textColor="#FF4367" />
        ) : adsError ? (
          <p style={{ color: 'red' }}>{adsError.message}</p>
        ) : (
          <div className="items-content">
            <div className="items-info">
              <p>Prikazanih <strong>{adsData.totalItems}</strong> oglasov</p>
              {isFetchingAds && !isLoadingAds && <span style={{ marginLeft: '10px', fontSize: '0.8em', color: '#555' }}>(Osvežujem...)</span>}
            </div>
            <div className="sort_items">
              <div className="left-controls">
                {appliedSearchTitle && (
                  <div className="search-info">
                    <span>Rezultati iskanja za: <strong>{appliedSearchTitle}</strong></span>
                    <button className="reset-search-btn" onClick={handleResetSearch}>✖</button>
                  </div>
                )}
              </div>
              <div className="right-controls">
                <select value={appliedSortOrder} onChange={handleSortChange}>
                  <option value="newest">Najnovejši</option>
                  <option value="oldest">Najstarejši</option>
                  <option value="price_asc">Cena: naraščajoče</option>
                  <option value="price_desc">Cena: padajoče</option>
                </select>
                <button className="view-toggle" onClick={toggleViewMode}>
                  {viewMode === 'list' ? <i className="fa-solid fa-th-large" /> : <i className="fa-solid fa-list" />}
                </button>
              </div>
            </div>

            <div className={`items ${viewMode}-view`}>
              {adsData.listings && adsData.listings.length > 0 ? (
                adsData.listings.map((item, index) => {
                  let image = null;
                  if (item.images && item.images.length > 0) {
                    // Try to find the primary image
                    const primaryImage = item.images.find(img => img.is_primary === true);

                    // If a primary image is found, use its URL.
                    // Otherwise, fall back to the first image in the array.
                    image = primaryImage ? primaryImage.image_url : item.images[0].image_url;
                  }
                  return (
                    <div
                      key={item.id} // Key on the direct child of map
                      className="item-card-wrapper"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <AdCard
                        // No need for key here if already on wrapper, but doesn't hurt
                        id={item.id}
                        images={item.images}
                        category={item.category?.name || 'N/A'}
                        title={item.title}
                        location={item.seller?.location || 'N/A'}
                        price={item.price}
                        created_at={item.created_at}
                        viewMode={viewMode}
                      />
                    </div>
                  );
                })
              ) : (
                // Show "no items" only if not actively fetching and listings are empty
                !isFetchingAds && adsData.listings && adsData.listings.length === 0 &&
                <div className="no-items-container">
                  <p className="no-items-message">
                    Ups! Trenutno ni oglasov, ki bi ustrezali vašim kriterijem.
                    Ste morda imeli odlično ponudbo, ki bi bila zanimiva za druge?
                  </p>
                  <button className="btn btn-success post-item-navbar-btn" onClick={() => navigate('/objavi-oglas')}>
                    Objavi svoj oglas
                  </button>
                </div>
              )}
            </div>

            <div className="pagination-container">
              <Pagination
                count={adsData.totalPages || 1}
                page={appliedPage}
                onChange={handlePageChange}
                color="primary"
                disabled={adsData.totalPages <= 1 || isFetchingAds} // Disable while fetching new page
              />
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default AdsPage;